/**
 * MfaEnrollmentInterstitial — the page a user lands on after password
 * verification when their tenant_users.mfa_enrollment_due is TRUE.
 *
 * SPRINT_SATURDAY_FINISH Stream 1.A. Three-step flow:
 *   1. Choose factor (TOTP or FIDO2)
 *   2. Enroll the factor
 *   3. Save backup codes + confirm
 *
 * After step 3 completes, calls the backend's password-retirement
 * endpoint (which sets tenant_users.password_retired_at and clears
 * mfa_enrollment_due) and redirects the user to the platform home.
 *
 * Soft-prompt mode: when matrix.required_factor_count === 1 (e.g.,
 * parent role), a "Skip for now" button appears that lets the user
 * proceed without enrolling. The mfa_enrollment_due flag stays TRUE
 * so they're prompted again next login. AAL2-mandatory roles
 * (power_user_staff, staff, teacher, vendor) do not see this button.
 *
 * @module admin/src/pages/auth/MfaEnrollmentInterstitial
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../api/client';

// =============================================================================
// TYPES
// =============================================================================

type FactorChoice = 'totp' | 'fido2' | null;
type Step = 'choose' | 'enroll' | 'backup_codes' | 'confirm';

interface PolicyInfo {
  matrix_role_name: string;
  required_factor_count: number;
  acceptable_factors: string[];
  fido2_attestation_policy: 'none' | 'verified_metadata' | 'allowlist';
}

interface TotpEnrollmentBegin {
  secret: string;
  otpauth_uri: string;
}

interface EnrollmentConfirmResponse {
  factor_id: string;
  backup_codes: string[];
  backup_generation_id: string;
}

// =============================================================================
// COMPONENT
// =============================================================================

export default function MfaEnrollmentInterstitial() {
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('choose');
  const [factor, setFactor] = useState<FactorChoice>(null);
  const [policy, setPolicy] = useState<PolicyInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // TOTP-specific state
  const [totpData, setTotpData] = useState<TotpEnrollmentBegin | null>(null);
  const [totpQrDataUrl, setTotpQrDataUrl] = useState<string | null>(null);
  const [totpCode, setTotpCode] = useState('');

  // Backup-codes state
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [acknowledgedBackup, setAcknowledgedBackup] = useState(false);

  // -----------------------------------------------------------------------
  // Load policy on mount so we know whether soft-skip is allowed and
  // which factors are acceptable.
  // -----------------------------------------------------------------------
  useEffect(() => {
    (async () => {
      try {
        const p = await apiClient.request('/api/v1/auth/mfa/policy/self');
        setPolicy(p as PolicyInfo);
      } catch (err) {
        // Non-fatal — render with default (hard-gate, both factors).
        // The backend enrollment endpoints will still enforce the
        // real policy.
        console.warn('Could not load enrollment policy', err);
      }
    })();
  }, []);

  // -----------------------------------------------------------------------
  // TOTP enrollment flow
  // -----------------------------------------------------------------------
  const beginTotpEnrollment = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiClient.request('/api/v1/auth/mfa/totp/enroll/begin', {
        method: 'POST',
      }) as TotpEnrollmentBegin;
      setTotpData(data);

      // Render QR code in browser using qrcode (admin dep). Lazy-import
      // so the auth bundle doesn't pull it for users who never enroll.
      const QRCode = await import('qrcode');
      const dataUrl = await QRCode.toDataURL(data.otpauth_uri, {
        errorCorrectionLevel: 'M',
        width: 256,
        margin: 1,
      });
      setTotpQrDataUrl(dataUrl);
    } catch (err: unknown) {
      setError((err as Error)?.message || 'Failed to start TOTP enrollment');
    } finally {
      setLoading(false);
    }
  }, []);

  const confirmTotpEnrollment = useCallback(async () => {
    if (!totpData || totpCode.length !== 6) {
      setError('Enter the 6-digit code from your authenticator app.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.request('/api/v1/auth/mfa/totp/enroll/confirm', {
        method: 'POST',
        body: JSON.stringify({
          secret: totpData.secret,
          code: totpCode,
          device_name: 'Authenticator App',
        }),
      }) as EnrollmentConfirmResponse;

      setBackupCodes(result.backup_codes);
      setStep('backup_codes');
    } catch (err: unknown) {
      // APIError shape from admin/src/api/client.js
      const e = err as { message?: string; status?: number };
      if (e?.status === 400) {
        setError('Code did not match. Check that your authenticator clock is accurate and try again.');
      } else {
        setError(e?.message || 'Enrollment failed');
      }
    } finally {
      setLoading(false);
    }
  }, [totpData, totpCode]);

  // -----------------------------------------------------------------------
  // FIDO2 enrollment flow
  // -----------------------------------------------------------------------
  const enrollFido2 = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const options = await apiClient.request('/api/v1/auth/mfa/fido2/enroll/options', {
        method: 'POST',
      });

      // @simplewebauthn/browser handles the navigator.credentials.create
      // call and the response transformation.
      const { startRegistration } = await import('@simplewebauthn/browser');
      const attResp = await startRegistration(options as Parameters<typeof startRegistration>[0]);

      const result = await apiClient.request('/api/v1/auth/mfa/fido2/enroll/confirm', {
        method: 'POST',
        body: JSON.stringify({
          response: attResp,
          device_name: 'Security Key',
        }),
      }) as { factor_id: string };

      // FIDO2 enrollment doesn't return backup codes (those are tied
      // to TOTP). Generate them separately so the recovery story
      // works regardless of factor type.
      const backup = await apiClient.request('/api/v1/auth/mfa/backup-codes/regenerate', {
        method: 'POST',
      }) as { backup_codes: string[] };

      setBackupCodes(backup.backup_codes);
      setStep('backup_codes');
      // Reference for lint:
      void result.factor_id;
    } catch (err: unknown) {
      const e = err as { message?: string; name?: string; status?: number };
      // WebAuthn cancellation: NotAllowedError. Surface a friendly
      // message rather than the raw DOMException.
      if (e?.name === 'NotAllowedError') {
        setError('Enrollment was cancelled. Try again or choose a different factor.');
      } else if (e?.status === 400) {
        // attestation_policy_violation surfaces here when the district
        // requires verified_metadata and the user's authenticator
        // isn't known to the FIDO MDS.
        setError('Your authenticator is not approved by district policy. Try a different device or contact your administrator.');
      } else {
        setError(e?.message || 'FIDO2 enrollment failed');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // -----------------------------------------------------------------------
  // Finalize: retire password, mark enrollment complete, redirect.
  // -----------------------------------------------------------------------
  const finishEnrollment = useCallback(async () => {
    if (!acknowledgedBackup) {
      setError('Confirm that you have saved the backup codes before continuing.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await apiClient.request('/api/v1/auth/mfa/enrollment/complete', { method: 'POST' });
      // Redirect to whatever page the user was originally trying to
      // reach. authService stashes the post-login redirect target.
      const dest = sessionStorage.getItem('postLoginRedirect') || '/';
      sessionStorage.removeItem('postLoginRedirect');
      navigate(dest, { replace: true });
    } catch (err: unknown) {
      setError((err as Error)?.message || 'Failed to finalize enrollment');
    } finally {
      setLoading(false);
    }
  }, [acknowledgedBackup, navigate]);

  const skipForNow = useCallback(() => {
    // Soft-gate mode only — the backend still has mfa_enrollment_due
    // set, so the user will see this page again next login. We just
    // let them proceed for this session.
    const dest = sessionStorage.getItem('postLoginRedirect') || '/';
    sessionStorage.removeItem('postLoginRedirect');
    navigate(dest, { replace: true });
  }, [navigate]);

  const downloadBackupCodes = useCallback(() => {
    const blob = new Blob(
      [
        `AgenticED MFA Backup Codes\n`,
        `Generated: ${new Date().toISOString()}\n`,
        `\n`,
        `Each code can be used ONCE to log in if you lose access to your authenticator.\n`,
        `Store these somewhere safe — they will not be shown again.\n`,
        `\n`,
        ...backupCodes.map(c => `${c}\n`),
      ],
      { type: 'text/plain' }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `agenticed-backup-codes-${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [backupCodes]);

  // -----------------------------------------------------------------------
  // RENDER
  // -----------------------------------------------------------------------
  const softGate = policy?.required_factor_count === 1;
  const totpAcceptable = !policy || policy.acceptable_factors.includes('totp');
  const fido2Acceptable = !policy || policy.acceptable_factors.includes('fido2');

  return (
    <div className='min-h-screen bg-slate-50 flex items-center justify-center px-4 py-12'>
      <div className='max-w-2xl w-full bg-white rounded-lg shadow-md p-8'>
        <h1 className='text-2xl font-bold text-slate-900 mb-2'>Set up multi-factor authentication</h1>
        <p className='text-sm text-slate-600 mb-6'>
          Your district&apos;s policy requires you to enroll a second authentication factor.
          {policy && (
            <span className='block mt-1 text-xs text-slate-500'>
              Role tier: <span className='font-medium'>{policy.matrix_role_name}</span>
              {' · '}
              Factors required: <span className='font-medium'>{policy.required_factor_count}</span>
            </span>
          )}
        </p>

        {error && (
          <div className='mb-4 p-3 rounded bg-red-50 border border-red-200 text-sm text-red-700'>
            {error}
          </div>
        )}

        {/* Step: choose factor ------------------------------------------- */}
        {step === 'choose' && (
          <div className='space-y-4'>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              {totpAcceptable && (
                <button
                  type='button'
                  onClick={() => { setFactor('totp'); setStep('enroll'); beginTotpEnrollment(); }}
                  className='text-left p-5 border border-slate-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition'
                >
                  <h3 className='font-semibold text-slate-900 mb-1'>Authenticator app (TOTP)</h3>
                  <p className='text-xs text-slate-500'>
                    Google Authenticator, 1Password, Authy, or any RFC 6238 app. Works on any phone.
                  </p>
                </button>
              )}
              {fido2Acceptable && (
                <button
                  type='button'
                  onClick={() => { setFactor('fido2'); setStep('enroll'); enrollFido2(); }}
                  className='text-left p-5 border border-slate-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition'
                >
                  <h3 className='font-semibold text-slate-900 mb-1'>Security key or biometric (FIDO2)</h3>
                  <p className='text-xs text-slate-500'>
                    YubiKey, Windows Hello, Touch ID, or any WebAuthn device. Phishing-resistant.
                  </p>
                  {policy?.fido2_attestation_policy === 'verified_metadata' && (
                    <p className='text-xs text-amber-700 mt-2'>
                      District requires a FIDO-certified authenticator.
                    </p>
                  )}
                </button>
              )}
            </div>

            {softGate && (
              <button
                type='button'
                onClick={skipForNow}
                className='block mx-auto text-sm text-slate-500 hover:text-slate-700 mt-4'
              >
                Skip for now (you&apos;ll be asked again next time)
              </button>
            )}
          </div>
        )}

        {/* Step: TOTP enrollment ----------------------------------------- */}
        {step === 'enroll' && factor === 'totp' && (
          <div className='space-y-4'>
            <h2 className='text-lg font-semibold text-slate-900'>1. Scan the QR code</h2>
            <p className='text-sm text-slate-600'>
              Open your authenticator app and add a new account by scanning this code.
            </p>
            {totpQrDataUrl && (
              <div className='flex justify-center'>
                <img src={totpQrDataUrl} alt='TOTP QR code' className='border border-slate-200 rounded' />
              </div>
            )}
            {totpData?.secret && (
              <details className='text-xs text-slate-500'>
                <summary className='cursor-pointer hover:text-slate-700'>Can&apos;t scan? Enter the secret manually</summary>
                <code className='block mt-2 p-2 bg-slate-100 rounded font-mono text-slate-700 break-all'>
                  {totpData.secret}
                </code>
              </details>
            )}

            <h2 className='text-lg font-semibold text-slate-900 pt-4'>2. Verify</h2>
            <p className='text-sm text-slate-600'>Enter the 6-digit code your app shows.</p>
            <input
              type='text'
              inputMode='numeric'
              maxLength={6}
              autoComplete='one-time-code'
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
              className='w-full px-4 py-3 border border-slate-300 rounded text-2xl font-mono tracking-widest text-center'
              placeholder='000000'
              disabled={loading}
            />

            <div className='flex gap-3 pt-2'>
              <button
                type='button'
                onClick={() => { setStep('choose'); setFactor(null); setTotpData(null); setTotpQrDataUrl(null); setTotpCode(''); }}
                className='px-4 py-2 text-sm text-slate-600 hover:text-slate-900'
                disabled={loading}
              >
                ← Back
              </button>
              <button
                type='button'
                onClick={confirmTotpEnrollment}
                disabled={loading || totpCode.length !== 6}
                className='ml-auto px-6 py-2 bg-blue-600 text-white rounded font-medium hover:bg-blue-700 disabled:opacity-50'
              >
                {loading ? 'Verifying…' : 'Verify and continue'}
              </button>
            </div>
          </div>
        )}

        {/* Step: FIDO2 enrollment (waiting for browser prompt) ----------- */}
        {step === 'enroll' && factor === 'fido2' && (
          <div className='text-center py-8'>
            <p className='text-slate-700 mb-2'>
              {loading
                ? 'Follow the prompt from your browser or security key.'
                : 'Click below to retry.'}
            </p>
            {!loading && (
              <button
                type='button'
                onClick={enrollFido2}
                className='mt-4 px-6 py-2 bg-blue-600 text-white rounded font-medium hover:bg-blue-700'
              >
                Try again
              </button>
            )}
            <button
              type='button'
              onClick={() => { setStep('choose'); setFactor(null); }}
              className='block mx-auto mt-2 text-sm text-slate-500 hover:text-slate-700'
            >
              Choose a different factor
            </button>
          </div>
        )}

        {/* Step: backup codes -------------------------------------------- */}
        {step === 'backup_codes' && (
          <div className='space-y-4'>
            <h2 className='text-lg font-semibold text-slate-900'>Save your backup codes</h2>
            <p className='text-sm text-slate-600'>
              Each code can be used ONCE to log in if you lose access to your authenticator.
              Store them in a password manager or print and lock in a drawer.
              <strong className='block mt-1'>They will not be shown again.</strong>
            </p>
            <div className='grid grid-cols-2 gap-2 p-4 bg-slate-50 rounded font-mono text-sm'>
              {backupCodes.map((code, i) => (
                <div key={i} className='px-2 py-1 bg-white border border-slate-200 rounded text-center'>
                  {code}
                </div>
              ))}
            </div>
            <button
              type='button'
              onClick={downloadBackupCodes}
              className='text-sm text-blue-600 hover:text-blue-700 font-medium'
            >
              Download as text file
            </button>

            <label className='flex items-start gap-2 pt-2'>
              <input
                type='checkbox'
                checked={acknowledgedBackup}
                onChange={(e) => setAcknowledgedBackup(e.target.checked)}
                className='mt-1'
              />
              <span className='text-sm text-slate-700'>
                I&apos;ve saved these backup codes somewhere safe.
              </span>
            </label>

            <button
              type='button'
              onClick={finishEnrollment}
              disabled={loading || !acknowledgedBackup}
              className='w-full px-6 py-3 bg-blue-600 text-white rounded font-medium hover:bg-blue-700 disabled:opacity-50'
            >
              {loading ? 'Finalizing…' : 'Continue to platform'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
